/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/destructuring-assignment */
import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useNavigate} from 'react-router-dom'
import {Controller, useForm} from 'react-hook-form'
import {joiResolver} from '@hookform/resolvers/joi'
import {AiOutlineInfoCircle} from 'react-icons/ai'
import {
  VmoCard,
  VmoCardContent,
  VmoForm,
  VmoFormCheckbox,
  VmoFormField,
  VmoFormGroup,
  VmoFormInput,
  VmoFormSelect,
  VmoFormTextArea,
  VmoTooltip,
} from 'vmo-library'

import {
  CREATE_TASK_AUTOMATION,
  EDIT_TASK_AUTOMATION,
  GET_STATUS_LIST,
  GET_TAGS,
  GET_TASK_AUTOMATION,
  GET_USERS,
} from 'actions/types'
import {getTags, getUsers} from 'actions/settings'
import {getStatusList} from 'actions/inbox'
import {createTaskAutomation, editTaskAutomation, getAllTaskAutomation, getTaskAutomation} from 'actions/settings_task'
import Page from 'components/common/Page'
import {priorityList, statusList} from 'constants/variables'
import {automationFormSchema} from 'validation/settings/tasks/task-automation.schema'
import useUserPermissions from 'hooks/pure/useUserPermission'
import useLearnMoreUrl from 'hooks/pure/useLearnMoreUrl'
import {getPathWithPrefix, removeDoubleQuotes} from 'utils/helper'
import {SETTINGS_PATH} from 'constants/path'
import useApiResponse from 'hooks/impure/useApiResponse'
import {Input, TextArea} from 'components/common/Form'
import {TASK_TYPE_OPTIONS} from 'constants/tasks'
import {useUnsavedChanges} from 'hooks/pure/useUnsavedChanges'
import ConditionalComponent from 'components/common/ConditionalComponent'

const triggerList = [
  {
    value: 'update_priority',
    text: 'Update Priority',
    key: 'update_priority',
  },
  {
    value: 'update_status',
    text: 'Update Status',
    key: 'update_status',
  },
  {
    value: 'update_assignee',
    text: 'Update Assignee',
    key: 'update_assignee',
  },
  {
    value: 'add_label',
    text: 'Add Label',
    key: 'add_label',
  },
]
const defaultValue = {
  name: '',
  entity: null,
  domain: '',
  trigger: null,
  labelId: null,
  statusId: null,
  priority: null,
  assigneeId: null,
  allowMultiple: false,
  task: {
    subject: '',
    type: 'activity',
    status: 'open',
    priority: 'high',
    assigneeId: null,
    description: '',
    duration: '',
  },
}

const AutomationForm = props => {
  const {editId, setNewTemplate} = props
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {
    handleSubmit,
    formState: {errors, isDirty},
    control,
    watch,
    reset,
  } = useForm({
    mode: 'onTouched',
    shouldFocusError: true,
    reValidateMode: 'onChange',
    submitFocusError: true,
    shouldUnregister: false,
    resolver: joiResolver(automationFormSchema),
    defaultValues: defaultValue,
  })
  const triggerValue = watch('trigger')

  const {taskAutomation} = useSelector(state => state.settingsTasks)
  const {getUsersList} = useSelector(state => state.settings)
  const {tagList} = useSelector(state => state.settings)
  const {statusList: emailInboxStatusList} = useSelector(state => state.emailInbox)
  const {type, isLoadingData, successLabels = []} = useSelector(state => state.apiReducer)

  const [labelOptionsState, setLabelOptionsState] = useState([])
  const [statusOptions, setStatusOptions] = useState([])
  const [usersList, setUsersList] = useState([])

  const {setWarningModal} = useUnsavedChanges()
  const {tasksPermissions} = useUserPermissions()
  const allowManageTaskAutomationsRole = tasksPermissions?.manage_task_automations

  const {settingsLearnMoreUrl} = useLearnMoreUrl()
  const addTaskAutomationLearnMore = settingsLearnMoreUrl?.tasks?.taskAutomation?.addLearnMore
  const updateTaskAutomationLearnMore = settingsLearnMoreUrl?.tasks?.taskAutomation?.updateLearnMore

  useApiResponse({
    action: getUsers,
    enabled: true,
    label: GET_USERS,
    storePath: 'settings.getUsersList',
    onSuccess: getUsersList => {
      const list = getUsersList?.users?.map(list => ({
        value: list.id,
        text: `${list.firstName} ${list.lastName}`,
        key: list.id,
      }))

      setUsersList(list || [])
    },
  })

  useApiResponse({
    action: getTags,
    enabled: true,
    label: GET_TAGS,
    storePath: 'settings.tagList',
    onSuccess: tagList => {
      const list = tagList?.map(label => ({
        value: label.id,
        key: label.id,
        text: label.label,
      }))
      setLabelOptionsState(list || [])
    },
  })

  useApiResponse({
    action: getStatusList,
    enabled: true,
    label: GET_STATUS_LIST,
    storePath: 'emailInbox.statusList',
    onSuccess: statusList => {
      const list = statusList?.map(item => ({
        value: item.id,
        key: item.id,
        text: item.status === 'on_hold' ? 'on hold' : item.status,
      }))
      setStatusOptions(list || [])
    },
  })

  const resetData = () => {
    const {name, entity, domain, trigger, labelId, statusId, priority, assigneeId, allowMultiple, task} = taskAutomation
    if (editId) {
      reset({
        name,
        entity,
        domain,
        trigger,
        labelId,
        statusId,
        priority,
        assigneeId,
        allowMultiple,
        task: {
          subject: task.subject,
          type: task.type,
          status: task.status,
          priority: task.priority,
          assigneeId: task.assigneeId,
          description: task.description,
          duration: task.duration,
        },
      })
      setWarningModal(false)
    } else {
      reset(defaultValue)
    }
  }

  const automationSubmit = data => {
    if (editId) {
      dispatch(editTaskAutomation(editId, data))
      setWarningModal(false)
    } else {
      dispatch(createTaskAutomation(data))
    }
  }

  useEffect(() => {
    if (editId) dispatch(getTaskAutomation(editId))
  }, [editId, dispatch])

  useEffect(() => {
    if (editId && isDirty) {
      setWarningModal(true)
    }
    return () => {
      setWarningModal(false)
    }
  }, [isDirty])

  useEffect(() => {
    if (successLabels.includes(CREATE_TASK_AUTOMATION)) {
      setNewTemplate(false)
      dispatch(getAllTaskAutomation())
    }
    if (successLabels.includes(EDIT_TASK_AUTOMATION)) {
      dispatch(getTaskAutomation(editId))
      navigate(getPathWithPrefix(SETTINGS_PATH.PREFIX, SETTINGS_PATH.TASK_AUTOMATION))
    }
    if (successLabels.includes(GET_TASK_AUTOMATION)) {
      resetData()
    }
  }, [successLabels])

  return (
    <>
      <Page
        header={editId ? 'Edit Task Automation' : 'Create Task Automation'}
        headerbackbtn={{
          onClick: () => {
            if (editId) {
              setWarningModal(false)
              navigate(getPathWithPrefix(SETTINGS_PATH.PREFIX, SETTINGS_PATH.TASK_AUTOMATION))
              resetData()
            } else {
              setNewTemplate(false)
            }
          },
        }}
        subHeader="Manage all automation rules related to task template"
        table={false}
        learnMoreUrl={editId ? updateTaskAutomationLearnMore : addTaskAutomationLearnMore}
        headerDivider
        lockRole={!allowManageTaskAutomationsRole}
        actionButton={{
          cancel: {
            content: 'Cancel',
            disabled: isLoadingData && (type.includes(EDIT_TASK_AUTOMATION) || type.includes(CREATE_TASK_AUTOMATION)),
            onClick: () => {
              if (editId) {
                setWarningModal(false)
                navigate(getPathWithPrefix(SETTINGS_PATH.PREFIX, SETTINGS_PATH.TASK_AUTOMATION))
                resetData()
              } else {
                setNewTemplate(false)
              }
            },
          },
          success: {
            content: editId ? 'Update' : 'Create',
            loading: isLoadingData && (type.includes(EDIT_TASK_AUTOMATION) || type.includes(CREATE_TASK_AUTOMATION)),
            disabled:
              !isDirty ||
              (isLoadingData && (type.includes(EDIT_TASK_AUTOMATION) || type.includes(CREATE_TASK_AUTOMATION))),
            ...(allowManageTaskAutomationsRole && {
              onClick: handleSubmit(data => automationSubmit(data)),
            }),
          },
        }}
      >
        <VmoForm className="errorLabel">
          <VmoCard fluid className="switchBox">
            <VmoCardContent>
              <Input
                name="name"
                control={control}
                label="Name Your Automated Action"
                placeholder="Type Name..."
                maxLength={20}
                width={8}
                required
                tooltipProps={{
                  content: 'Name your Automated Action',
                }}
              />
            </VmoCardContent>
          </VmoCard>

          <VmoCard fluid className="switchBox">
            <VmoCardContent>
              <h4 className="mt-0 mb-1">Specify Your Trigger (Entity and Trigger)</h4>
              <p className="vmo-lead mb-4">Specify the conditions of entity and trigger for your task automation.</p>
              <VmoFormField required>
                <div className="info-header">
                  <label>Entity</label>
                  <VmoTooltip content="Set an entity for task automation" trigger={<AiOutlineInfoCircle />} />
                </div>
                <Controller
                  control={control}
                  name="entity"
                  render={({field: {value, onChange, onBlur}}) => (
                    <VmoFormSelect
                      width={8}
                      required
                      placeholder="Select entity..."
                      options={[
                        {
                          value: 'conversation',
                          text: 'Conversation',
                          key: 'conversation',
                        },
                      ]}
                      onChange={(e, {value}) => onChange(value)}
                      value={value}
                      onBlur={onBlur}
                      error={
                        errors.entity && {
                          content: errors.entity.message,
                        }
                      }
                    />
                  )}
                />
              </VmoFormField>
              <Input
                name="domain"
                control={control}
                label="Domain"
                placeholder="Enter domain"
                maxLength={30}
                width={8}
                required
                tooltipProps={{
                  content: 'Specify a domain',
                }}
              />
              <VmoFormField required>
                <div className="info-header">
                  <label>Trigger</label>
                  <VmoTooltip content="Set trigger condition for automation" trigger={<AiOutlineInfoCircle />} />
                </div>
                <Controller
                  control={control}
                  name="trigger"
                  render={({field: {value, onChange, onBlur}}) => (
                    <VmoFormSelect
                      width={8}
                      placeholder="Select trigger ..."
                      value={value}
                      options={triggerList}
                      onChange={(e, {value}) => onChange(value)}
                      onBlur={onBlur}
                      error={
                        errors.trigger && {
                          content: <span>{errors.trigger.message}</span>,
                        }
                      }
                    />
                  )}
                />
                <ConditionalComponent
                  conditions={[
                    {
                      condition: triggerValue === 'update_priority',
                      component: () => (
                        <Controller
                          control={control}
                          name="priority"
                          render={({field: {value, onChange, onBlur}}) => (
                            <VmoFormSelect
                              width={8}
                              label="Priority"
                              options={priorityList}
                              value={value}
                              onChange={(e, {value}) => onChange(value)}
                              onBlur={onBlur}
                              error={
                                errors.priority && {
                                  content: errors.priority.message,
                                }
                              }
                            />
                          )}
                        />
                      ),
                    },
                    {
                      condition: triggerValue === 'update_status',
                      component: () => (
                        <Controller
                          control={control}
                          name="statusId"
                          render={({field: {value, onChange, onBlur}}) => (
                            <VmoFormSelect
                              width={8}
                              label="Status"
                              options={statusOptions}
                              value={value}
                              onChange={(e, {value}) => onChange(value)}
                              onBlur={onBlur}
                              error={
                                errors.statusId && {
                                  content: errors.statusId.message,
                                }
                              }
                            />
                          )}
                        />
                      ),
                    },
                    {
                      condition: triggerValue === 'update_assignee',
                      component: () => (
                        <Controller
                          control={control}
                          name="assigneeId"
                          render={({field: {value, onChange, onBlur}}) => (
                            <VmoFormSelect
                              width={8}
                              label="Assignee"
                              options={usersList}
                              value={value}
                              onChange={(e, {value}) => onChange(value)}
                              onBlur={onBlur}
                              error={
                                errors.assigneeId && {
                                  content: errors.assigneeId.message,
                                }
                              }
                            />
                          )}
                        />
                      ),
                    },
                    {
                      condition: triggerValue === 'add_label',
                      component: () => (
                        <Controller
                          control={control}
                          name="labelId"
                          render={({field: {value, onChange, onBlur}}) => (
                            <VmoFormSelect
                              width={8}
                              label="Label"
                              options={labelOptionsState}
                              value={value}
                              onChange={(e, {value}) => onChange(value)}
                              onBlur={onBlur}
                              error={
                                errors.labelId && {
                                  content: errors.labelId.message,
                                }
                              }
                            />
                          )}
                        />
                      ),
                    },
                  ]}
                />
              </VmoFormField>

              <Controller
                control={control}
                name="allowMultiple"
                render={({field: {value, onChange, onBlur}}) => (
                  <VmoFormCheckbox
                    label="Allow Multiple "
                    checked={value}
                    options={triggerList}
                    onChange={(e, {checked}) => onChange(checked)}
                    onBlur={onBlur}
                    error={
                      errors.allowMultiple && {
                        content: errors.allowMultiple.message,
                      }
                    }
                  />
                )}
              />
            </VmoCardContent>
          </VmoCard>
          <VmoCard fluid className="switchBox">
            <VmoCardContent>
              <h4 className="mt-0 mb-1">Set Tasks </h4>
              <p className="vmo-lead mb-4">Set tasks when your automation is triggered.</p>

              <VmoFormGroup widths="equal">
                <Input
                  name="task.subject"
                  control={control}
                  label="Task Subject"
                  placeholder="Add name..."
                  maxLength={30}
                  required
                  tooltipProps={{
                    content: 'Add a subject for the task',
                  }}
                />
                <VmoFormField>
                  <div className="info-header">
                    <label>Task Type</label>
                    <VmoTooltip content="Set type of the task for automation" trigger={<AiOutlineInfoCircle />} />
                  </div>
                  <Controller
                    control={control}
                    name="task.type"
                    render={({field: {value, onChange, onBlur}}) => (
                      <VmoFormSelect
                        placeholder="Select Task Type ..."
                        options={TASK_TYPE_OPTIONS}
                        required
                        value={value}
                        onChange={(e, {value}) => onChange(value)}
                        onBlur={onBlur}
                        error={
                          errors?.task?.type && {
                            content: errors.task.type.message,
                          }
                        }
                      />
                    )}
                  />
                </VmoFormField>
              </VmoFormGroup>

              <VmoFormGroup widths="equal">
                <VmoFormField>
                  <div className="info-header">
                    <label>Owner</label>
                    <VmoTooltip content="Assign an agent for the task" trigger={<AiOutlineInfoCircle />} />
                  </div>
                  <Controller
                    control={control}
                    name="task.assigneeId"
                    render={({field: {value, onChange, onBlur}}) => (
                      <VmoFormSelect
                        value={value}
                        placeholder="Select Owner ..."
                        options={usersList}
                        onChange={(e, {value}) => onChange(value)}
                        onBlur={onBlur}
                        error={
                          errors?.task?.assigneeId && {
                            content: errors.task.assigneeId.message,
                          }
                        }
                      />
                    )}
                  />
                </VmoFormField>
                <VmoFormField>
                  <div className="info-header">
                    <label>Priority</label>
                    <VmoTooltip content="Set priority of the task" trigger={<AiOutlineInfoCircle />} />
                  </div>
                  <Controller
                    control={control}
                    name="task.priority"
                    render={({field: {value, onChange, onBlur}}) => (
                      <VmoFormSelect
                        placeholder="Select Priority..."
                        options={priorityList}
                        onChange={(e, {value}) => onChange(value)}
                        onBlur={onBlur}
                        value={value}
                        error={
                          errors?.task?.priority && {
                            content: errors.task.priority.message,
                          }
                        }
                      />
                    )}
                  />
                </VmoFormField>
              </VmoFormGroup>

              <VmoFormGroup widths="equal">
                <VmoFormField>
                  <div className="info-header">
                    <label>Status</label>
                    <VmoTooltip content="Set status of the task" trigger={<AiOutlineInfoCircle />} />
                  </div>
                  <Controller
                    control={control}
                    name="task.status"
                    render={({field: {value, onChange, onBlur}}) => (
                      <VmoFormSelect
                        placeholder="Select status ..."
                        options={statusList}
                        onChange={(e, {value}) => onChange(value)}
                        onBlur={onBlur}
                        value={value}
                        error={
                          errors?.task?.status && {
                            content: errors.task.status.message,
                          }
                        }
                      />
                    )}
                  />
                </VmoFormField>

                <Input
                  name="task.duration"
                  control={control}
                  label="Duration"
                  tooltipProps={{content: 'Specify duration of task'}}
                  placeholder="number of days"
                  type="number"
                  required
                />
              </VmoFormGroup>
              <TextArea
                name="task.description"
                control={control}
                label="Description"
                tooltipProps={{content: 'Specify brief description about the task'}}
                maxLength={200}
                placeholder="Select Description..."
              />
            </VmoCardContent>
          </VmoCard>
        </VmoForm>
      </Page>
    </>
  )
}

export default AutomationForm
